import React, { useEffect, useState } from "react";
import api, { fmtDateTime, fmtINR } from "../lib/api";
import { Page, Field, StatusPill } from "../components/Primitives";
import { toast } from "sonner";
import { Plus, X, FileText, CreditCard, Check, Trash2, Wallet } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const emptyLine = { description: "", qty: 1, rate: 0, discount: 0, gst_rate: 18 };
const emptyInv = {
  kind: "customer",
  customer_name: "", customer_phone: "", customer_gstin: "", customer_address: "",
  vehicle_reg: "", lines: [{ ...emptyLine }], notes: "",
  place_of_supply: "Karnataka", business_state: "Karnataka",
};

export default function Accounting() {
  const [invoices, setInvoices] = useState([]);
  const [kindFilter, setKindFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyInv);
  const [payFor, setPayFor] = useState(null);
  const [payForm, setPayForm] = useState({ amount: 0, mode: "cash", note: "" });

  const load = async () => {
    const { data } = await api.get("/invoices", {
      params: { kind: kindFilter || undefined, status: statusFilter || undefined },
    });
    setInvoices(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kindFilter, statusFilter]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/invoices", {
        ...form,
        lines: form.lines.map((l) => ({ ...l, qty: +l.qty, rate: +l.rate, discount: +l.discount, gst_rate: +l.gst_rate })),
      });
      toast.success("Invoice created");
      setOpen(false); setForm(emptyInv); load();
    } catch (err) { toast.error("Failed"); }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setLine = (i, patch) => {
    const ls = [...form.lines]; ls[i] = { ...ls[i], ...patch }; setForm({ ...form, lines: ls });
  };
  const addLine = () => setForm({ ...form, lines: [...form.lines, { ...emptyLine }] });
  const delLine = (i) => setForm({ ...form, lines: form.lines.filter((_, j) => j !== i) });

  const markPaid = async (id) => {
    const fd = new FormData(); fd.append("invoice_id", id); fd.append("mode", "cash");
    await api.post("/payments/mark-paid", fd);
    toast.success("Marked paid"); load();
  };

  const openPartial = (inv) => {
    const grand = Number(inv.grand_total || 0);
    const paid = Number(inv.amount_paid || 0);
    const balance = Math.max(grand - paid, 0);
    setPayFor(inv);
    setPayForm({ amount: balance, mode: "cash", note: paid === 0 ? "Advance" : "Part payment" });
  };

  const savePartial = async (e) => {
    e.preventDefault();
    try {
      const fd = new FormData();
      fd.append("invoice_id", payFor.id);
      fd.append("amount", String(payForm.amount));
      fd.append("mode", payForm.mode);
      fd.append("note", payForm.note || "");
      const { data } = await api.post("/payments/partial", fd);
      toast.success(data.status === "paid" ? "Fully settled" : `Part payment recorded · balance ${fmtINR(data.balance_due)}`);
      setPayFor(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to record payment");
    }
  };

  const razorpayPay = async (inv) => {
    const fd = new FormData(); fd.append("invoice_id", inv.id);
    const { data } = await api.post("/payments/razorpay/order", fd);
    if (data.mock) {
      // Mock flow — auto-verify
      const vfd = new FormData();
      vfd.append("invoice_id", inv.id);
      vfd.append("razorpay_order_id", data.order_id);
      vfd.append("razorpay_payment_id", "pay_MOCK_" + Math.random().toString(36).slice(2, 10));
      await api.post("/payments/razorpay/verify", vfd);
      toast.success("Mock payment captured (configure Razorpay keys in backend/.env to go live)");
      load();
      return;
    }
    // Live Razorpay checkout
    const options = {
      key: data.key_id,
      amount: data.amount,
      currency: "INR",
      name: "Karr Kraft",
      description: inv.code,
      order_id: data.order_id,
      handler: async (res) => {
        const vfd = new FormData();
        vfd.append("invoice_id", inv.id);
        vfd.append("razorpay_order_id", res.razorpay_order_id);
        vfd.append("razorpay_payment_id", res.razorpay_payment_id);
        vfd.append("razorpay_signature", res.razorpay_signature);
        await api.post("/payments/razorpay/verify", vfd);
        toast.success("Payment captured");
        load();
      },
      prefill: { name: inv.customer_name, contact: inv.customer_phone },
      theme: { color: "#CCFF00" },
    };
    if (!window.Razorpay) {
      toast.error("Razorpay SDK not loaded");
      return;
    }
    new window.Razorpay(options).open();
  };

  return (
    <Page
      number="06"
      title="Accounting & Invoicing"
      subtitle="Internal invoices against vehicles + customer GST invoices with Razorpay."
      right={<button className="kk-btn-primary" onClick={() => setOpen(true)} data-testid="new-invoice"><Plus size={14}/> New Invoice</button>}
    >
      <div className="flex flex-wrap gap-2 mb-5 items-center">
        <select className="kk-input max-w-xs" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} data-testid="kind-filter">
          <option value="">All kinds</option>
          <option value="customer">Customer</option>
          <option value="internal">Internal</option>
        </select>
        <select className="kk-input max-w-xs" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="status-filter">
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="paid">Paid</option>
          <option value="partial">Partial</option>
        </select>
      </div>

      <table className="kk-table">
        <thead>
          <tr>
            <th>#</th><th>Customer</th><th>Kind</th><th>Vehicle</th><th>Total</th><th>Status</th><th>Created</th><th></th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((i) => (
            <tr key={i.id} data-testid={`inv-row-${i.id}`}>
              <td className="font-mono">{i.code}</td>
              <td>{i.customer_name}<div className="text-xs text-[var(--kk-muted)]">{i.customer_phone}</div></td>
              <td><span className="kk-pill kk-pill-muted">{i.kind}</span></td>
              <td className="font-mono text-xs">{i.vehicle_reg || "—"}</td>
              <td className="font-mono">
                {fmtINR(i.grand_total)}
                {Number(i.amount_paid || 0) > 0 && Number(i.balance_due ?? (i.grand_total - (i.amount_paid || 0))) > 0 && (
                  <div className="text-[0.65rem] text-[var(--kk-muted)]" data-testid={`inv-balance-${i.id}`}>
                    Paid {fmtINR(i.amount_paid)} · Due {fmtINR(i.balance_due ?? (i.grand_total - i.amount_paid))}
                  </div>
                )}
              </td>
              <td><StatusPill status={i.status} /></td>
              <td className="font-mono text-xs">{fmtDateTime(i.created_at)}</td>
              <td>
                <div className="flex gap-1 flex-wrap">
                  <a href={`${BACKEND}/api/invoices/${i.id}/pdf`} target="_blank" rel="noreferrer" className="kk-pill kk-pill-muted" data-testid={`pdf-${i.id}`}><FileText size={11}/> PDF</a>
                  {i.status !== "paid" && (
                    <>
                      <button onClick={() => razorpayPay(i)} className="kk-pill kk-pill-black" data-testid={`rz-${i.id}`}><CreditCard size={11}/> Pay</button>
                      <button onClick={() => openPartial(i)} className="kk-pill kk-pill-muted" data-testid={`partial-${i.id}`} title="Record advance / part payment"><Wallet size={11}/> Part-Pay</button>
                      <button onClick={() => markPaid(i.id)} className="kk-pill kk-pill-lime" data-testid={`paid-${i.id}`}><Check size={11}/> Paid</button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
          {invoices.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-[var(--kk-muted)]">No invoices.</td></tr>}
        </tbody>
      </table>

      {open && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={() => setOpen(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl bg-[var(--kk-surface)] p-8 overflow-y-auto">
            <div className="flex items-center justify-between"><h2 className="font-display text-2xl uppercase">New Invoice</h2><button type="button" onClick={() => setOpen(false)}><X /></button></div>
            <div className="grid grid-cols-2 gap-5 mt-5">
              <Field label="Kind"><select className="kk-input" value={form.kind} onChange={upd("kind")}><option value="customer">Customer</option><option value="internal">Internal</option></select></Field>
              <Field label="Vehicle reg #"><input className="kk-input" value={form.vehicle_reg} onChange={upd("vehicle_reg")} /></Field>
              <Field label="Customer name *"><input className="kk-input" required value={form.customer_name} onChange={upd("customer_name")} data-testid="inv-cust-name" /></Field>
              <Field label="Customer phone"><input className="kk-input" value={form.customer_phone} onChange={upd("customer_phone")} /></Field>
              <Field label="Customer GSTIN"><input className="kk-input" value={form.customer_gstin} onChange={upd("customer_gstin")} /></Field>
              <Field label="Place of supply"><input className="kk-input" value={form.place_of_supply} onChange={upd("place_of_supply")} /></Field>
              <div className="col-span-2"><Field label="Address"><textarea className="kk-input min-h-[60px]" value={form.customer_address} onChange={upd("customer_address")} /></Field></div>
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <div className="kk-overline">Lines</div>
                <button type="button" onClick={addLine} className="kk-pill kk-pill-black" data-testid="inv-add-line"><Plus size={12}/> Add</button>
              </div>
              <table className="kk-table">
                <thead><tr><th>Description</th><th>Qty</th><th>Rate</th><th>GST%</th><th></th></tr></thead>
                <tbody>
                  {form.lines.map((l, idx) => (
                    <tr key={idx}>
                      <td><input className="kk-input py-1 text-sm" required value={l.description} onChange={(e) => setLine(idx, { description: e.target.value })} /></td>
                      <td><input className="kk-input py-1 text-sm w-16" type="number" value={l.qty} onChange={(e) => setLine(idx, { qty: e.target.value })} /></td>
                      <td><input className="kk-input py-1 text-sm w-24" type="number" value={l.rate} onChange={(e) => setLine(idx, { rate: e.target.value })} /></td>
                      <td><input className="kk-input py-1 text-sm w-16" type="number" value={l.gst_rate} onChange={(e) => setLine(idx, { gst_rate: e.target.value })} /></td>
                      <td><button type="button" onClick={() => delLine(idx)}><Trash2 size={14}/></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button className="kk-btn-primary mt-6" type="submit" data-testid="save-invoice">Create Invoice</button>
          </form>
        </div>
      )}

      {payFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setPayFor(null)}>
          <form onSubmit={savePartial} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[var(--kk-surface)] p-6 md:p-8">
            <div className="flex items-start justify-between">
              <div>
                <div className="kk-overline">Record payment</div>
                <h2 className="font-display text-2xl uppercase">{payFor.code}</h2>
                <div className="text-xs text-[var(--kk-muted)] mt-1">{payFor.customer_name}</div>
              </div>
              <button type="button" onClick={() => setPayFor(null)}><X /></button>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-3">
              <div>
                <div className="kk-overline text-[0.55rem]">Grand total</div>
                <div className="font-mono font-semibold">{fmtINR(payFor.grand_total)}</div>
              </div>
              <div>
                <div className="kk-overline text-[0.55rem]">Already paid</div>
                <div className="font-mono font-semibold">{fmtINR(payFor.amount_paid || 0)}</div>
              </div>
              <div>
                <div className="kk-overline text-[0.55rem]">Outstanding</div>
                <div className="font-mono font-semibold text-red-600" data-testid="pp-balance">
                  {fmtINR(Math.max((payFor.grand_total || 0) - (payFor.amount_paid || 0), 0))}
                </div>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-4">
              <Field label="Amount (INR) *">
                <input className="kk-input" required type="number" step="0.01" min="0.01"
                  max={(payFor.grand_total || 0) - (payFor.amount_paid || 0)}
                  value={payForm.amount}
                  onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                  data-testid="pp-amount" autoFocus />
              </Field>
              <Field label="Mode *">
                <select className="kk-input" value={payForm.mode} onChange={(e) => setPayForm({ ...payForm, mode: e.target.value })} data-testid="pp-mode">
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="card">Card</option>
                  <option value="bank_transfer">Bank transfer</option>
                  <option value="cheque">Cheque</option>
                </select>
              </Field>
              <div className="col-span-2">
                <Field label="Note (optional)">
                  <input className="kk-input" value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })}
                    placeholder="e.g. Advance on job card, UTR number…" data-testid="pp-note" />
                </Field>
              </div>
            </div>

            {(payFor.payments || []).length > 0 && (
              <div className="mt-5">
                <div className="kk-overline mb-2">Payment history</div>
                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                  {payFor.payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between text-xs font-mono border-b border-[var(--kk-border)] pb-1">
                      <span>{(p.at || "").slice(0, 10)} · {p.mode}</span>
                      <span className="font-semibold">{fmtINR(p.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2 mt-6">
              <button type="submit" className="kk-btn-primary" data-testid="pp-save"><Wallet size={14}/> Record payment</button>
              <button type="button" className="kk-btn-ghost" onClick={() => setPayFor(null)}>Cancel</button>
            </div>
          </form>
        </div>
      )}
    </Page>
  );
}
